import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "../App";
import { api } from "../services/apiClient";
import { canPublishPropertyRequests } from "../constants/plans";
import { MICROCOPY } from "../constants/microcopy";
import BackNavigationButton from "../components/navigation/BackNavigationButton";
import { useFormDraft } from "../hooks/useFormDraft";
import { useUnsavedChangesGuard } from "../hooks/useUnsavedChangesGuard";

const createInitialFormData = () => ({
  property_type: "",
  location: "",
  description: "",
  budget_min: "",
  budget_max: "",
  required_features: "",
  desired_features: "",
  flexible_features: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  notes: "",
});

const CreatePropertyRequest = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(createInitialFormData());
  const draftKey = useMemo(
    () => `draft:create-property-request:${user?.id || user?.email || "anon"}`,
    [user],
  );
  const isDirty = useMemo(
    () => JSON.stringify(formData) !== JSON.stringify(createInitialFormData()),
    [formData],
  );
  const { clearDraft } = useFormDraft({
    draftKey,
    formData,
    setFormData,
    enabled: Boolean(user),
  });
  const { confirmIfDirty } = useUnsavedChangesGuard(isDirty);

  // Verificar permisos al cargar
  useEffect(() => {
    if (!user) {
      toast.error("Debes iniciar sesión para publicar búsquedas");
      navigate("/login");
      return;
    }

    if (!canPublishPropertyRequests(user)) {
      toast.error("Publicar búsquedas requiere Plan Pro, Broker o Developer");
      navigate("/pricing");
      return;
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const requestData = {
        ...formData,
        budget_min: parseFloat(formData.budget_min),
        budget_max: parseFloat(formData.budget_max),
        required_features: formData.required_features
          .split(",")
          .map((f) => f.trim())
          .filter((f) => f),
        desired_features: formData.desired_features
          .split(",")
          .map((f) => f.trim())
          .filter((f) => f),
        flexible_features: formData.flexible_features
          .split(",")
          .map((f) => f.trim())
          .filter((f) => f),
      };

      await api.post("/property-requests", requestData);

      toast.success("¡Solicitud publicada!");
      clearDraft();
      navigate("/property-requests");
    } catch (error) {
      toast.error("Error al publicar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted">
      <Header />

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-4">
          <BackNavigationButton
            fallbackPath="/property-requests"
            onBeforeNavigate={confirmIfDirty}
            dataTestId="property-request-back"
          />
        </div>
        <h1 className="text-4xl font-bold mb-8">Publicar Necesidad de Propiedad</h1>

        <div className="bg-background border border-border rounded-lg p-5 mb-6">
          <p className="text-sm font-semibold">{MICROCOPY.requestForm.headline}</p>
          <p className="text-sm text-muted-foreground mt-1">
            {MICROCOPY.requestForm.subheadline}
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-background rounded-lg p-6 space-y-6"
        >
          <div>
            <label className="text-sm font-medium mb-2 block">
              Tipo de Propiedad *
            </label>
            <Select
              value={formData.property_type}
              onValueChange={(v) =>
                setFormData({ ...formData, property_type: v })
              }
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Residencial">Residencial</SelectItem>
                <SelectItem value="Comercial">Comercial</SelectItem>
                <SelectItem value="Industrial">Industrial</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">
              Ubicación Deseada *
            </label>
            <Input
              value={formData.location}
              onChange={(e) =>
                setFormData({ ...formData, location: e.target.value })
              }
              placeholder="Ej: San Pedro Garza García, NL"
              required
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">
              Descripción de lo que Buscas *
            </label>
            <Textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Ej: Busco casa familiar con jardín amplio en zona segura para vivir con mis hijos..."
              rows={3}
              required
            />
            <p className="text-xs text-muted-foreground mt-1">
              Describe qué estás buscando y por qué - esto ayuda a recibir
              mejores ofertas
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">
                Presupuesto Mínimo (MXN) *
              </label>
              <Input
                type="number"
                value={formData.budget_min}
                onChange={(e) =>
                  setFormData({ ...formData, budget_min: e.target.value })
                }
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Presupuesto Máximo (MXN) *
              </label>
              <Input
                type="number"
                value={formData.budget_max}
                onChange={(e) =>
                  setFormData({ ...formData, budget_max: e.target.value })
                }
                required
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground -mt-2">
            {MICROCOPY.requestForm.budgetHint}
          </p>

          <div>
            <label className="text-sm font-medium mb-2 block">
              Características Requeridas (separadas por comas)
            </label>
            <Textarea
              value={formData.required_features}
              onChange={(e) =>
                setFormData({ ...formData, required_features: e.target.value })
              }
              placeholder="Ej: 3 recámaras, 2 baños, jardín"
              rows={3}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {MICROCOPY.requestForm.featureHints.required}
            </p>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">
              Características Deseadas (separadas por comas)
            </label>
            <Textarea
              value={formData.desired_features}
              onChange={(e) =>
                setFormData({ ...formData, desired_features: e.target.value })
              }
              placeholder="Ej: alberca, gym, terraza"
              rows={3}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {MICROCOPY.requestForm.featureHints.desired}
            </p>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">
              Características Flexibles (separadas por comas)
            </label>
            <Textarea
              value={formData.flexible_features}
              onChange={(e) =>
                setFormData({ ...formData, flexible_features: e.target.value })
              }
              placeholder="Ej: estacionamiento, balcón"
              rows={3}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {MICROCOPY.requestForm.featureHints.flexible}
            </p>
          </div>

          <div className="border-t pt-6">
            <h3 className="font-bold mb-4">Información de Contacto</h3>
            <p className="text-sm text-muted-foreground mb-4">
              🔒 <strong>Privacidad:</strong> {MICROCOPY.requestForm.contactPrivacy}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                placeholder="Nombre *"
                value={formData.contact_name}
                onChange={(e) =>
                  setFormData({ ...formData, contact_name: e.target.value })
                }
                required
              />
              <Input
                type="email"
                placeholder="Email *"
                value={formData.contact_email}
                onChange={(e) =>
                  setFormData({ ...formData, contact_email: e.target.value })
                }
                required
              />
              <Input
                type="tel"
                placeholder="Teléfono *"
                value={formData.contact_phone}
                onChange={(e) =>
                  setFormData({ ...formData, contact_phone: e.target.value })
                }
                required
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">
              Notas Adicionales
            </label>
            <Textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              placeholder="Ej: fecha objetivo de compra, restricciones legales, tipo de operación preferida"
              rows={4}
            />
          </div>

          <Button type="submit" disabled={loading} className="w-full">
            {loading ? "Publicando..." : "Publicar Búsqueda"}
          </Button>
        </form>
      </div>

      <Footer />
    </div>
  );
};

export default CreatePropertyRequest;
